import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Info, Upload } from "lucide-react";
import "../SimulationPage.css";

let tooltipIdCounter = 0;

function useStableTooltipId() {
  const idRef = useRef("");

  if (!idRef.current) {
    tooltipIdCounter += 1;
    idRef.current =
      `simulator-tooltip-${tooltipIdCounter}`;
  }

  return idRef.current;
}

export function SimulatorHeader({
  eyebrow,
  title,
  subtitle,
}) {
  return (
    <>
      {eyebrow && (
        <p className="simulator__eyebrow">
          {eyebrow}
        </p>
      )}

      <h1 className="simulator__title">
        {title}
      </h1>

      {subtitle && (
        <p className="simulator__subtitle">
          {subtitle}
        </p>
      )}
    </>
  );
}

export function ReuseCard({
  title,
  description,
  browseLabel,
  applyLabel,
  onApply,
}) {
  const fileInputRef = useRef(null);

  const [file, setFile] =
    useState(null);

  const [applying, setApplying] =
    useState(false);

  const [message, setMessage] =
    useState("");

  const [error, setError] =
    useState("");

  const openFilePicker = () => {
    /*
     * Reset the native input value so selecting the same
     * file again still triggers onChange.
     */
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const selectedFile =
      event.target.files?.[0] ||
      null;

    setFile(selectedFile);
    setMessage("");
    setError("");
  };

  const handleApply = async () => {
    if (
      !file ||
      !onApply ||
      applying
    ) {
      return;
    }

    try {
      setApplying(true);
      setMessage("");
      setError("");

      await onApply(file);

      setMessage(
        "Inputs applied successfully.",
      );
    } catch (applyError) {
      console.error(
        "[LEG-UP] Could not apply uploaded simulation:",
        applyError,
      );

      setError(
        applyError?.message ||
          "The uploaded simulation could not be applied.",
      );
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="simulator__card simulator__reuse-card">
      <button
        type="button"
        className="simulator__reuse-icon"
        onClick={openFilePicker}
        aria-label={browseLabel}
      >
        <Upload size={18} />
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx"
        onChange={handleFileChange}
        hidden
      />

      <div className="simulator__reuse-text">
        <span className="simulator__reuse-title">
          {title}
        </span>

        <span className="simulator__reuse-desc">
          {description}
        </span>

        {file && (
          <span className="simulator__reuse-file">
            {file.name}
          </span>
        )}

        {message && (
          <span
            className="simulator__reuse-file"
            role="status"
          >
            {message}
          </span>
        )}

        {error && (
          <span
            className="simulator__reuse-file"
            role="alert"
            style={{
              color: "#9b3b2f",
            }}
          >
            {error}
          </span>
        )}
      </div>

      <button
        type="button"
        className="simulator__btn-outline"
        onClick={openFilePicker}
      >
        {browseLabel}
      </button>

      <button
        type="button"
        className="simulator__btn-primary"
        onClick={handleApply}
        disabled={
          !file ||
          !onApply ||
          applying
        }
      >
        {applying
          ? "Applying..."
          : applyLabel}
      </button>
    </div>
  );
}

export function FieldWithIcon({
  icon: Icon,
  unit,
  children,
}) {
  return (
    <div
      className={[
        "simulator__field-input-wrapper",
        !Icon
          ? "simulator__field-input-wrapper--plain"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {Icon && (
        <span className="simulator__field-icon">
          <Icon size={16} />
        </span>
      )}

      {children}

      {unit && (
        <span className="simulator__field-unit">
          {unit}
        </span>
      )}
    </div>
  );
}

export function CircleProgress({
  percentage,
  label,
}) {
  const safePercentage = Math.max(
    0,
    Math.min(
      Number(percentage) || 0,
      100,
    ),
  );

  const radius = 50;
  const circumference =
    2 * Math.PI * radius;

  const offset =
    circumference -
    (safePercentage / 100) *
      circumference;

  return (
    <div className="simulator__circle-wrapper">
      <svg
        className="simulator__circle-svg"
        viewBox="0 0 120 120"
        aria-hidden="true"
      >
        <circle
          className="simulator__circle-bg"
          cx="60"
          cy="60"
          r={radius}
        />

        <circle
          className="simulator__circle-progress"
          cx="60"
          cy="60"
          r={radius}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform="rotate(-90 60 60)"
        />

        <text
          className="simulator__circle-text"
          x="60"
          y="65"
          textAnchor="middle"
        >
          {safePercentage}%
        </text>
      </svg>

      <span className="simulator__circle-label">
        {label}
      </span>
    </div>
  );
}

const TIP_MAX_WIDTH = 260;
const TIP_EDGE = 12;

export function InfoTip({ text }) {
  const [open, setOpen] =
    useState(false);

  const [position, setPosition] =
    useState(null);

  const triggerRef = useRef(null);
  const id = useStableTooltipId();

  useLayoutEffect(() => {
    if (
      !open ||
      !triggerRef.current
    ) {
      return;
    }

    const rect =
      triggerRef.current.getBoundingClientRect();

    const width = Math.min(
      TIP_MAX_WIDTH,
      Math.max(
        0,
        window.innerWidth -
          2 * TIP_EDGE,
      ),
    );

    const center =
      rect.left +
      rect.width / 2;

    const left = Math.min(
      Math.max(
        center - width / 2,
        TIP_EDGE,
      ),
      window.innerWidth -
        TIP_EDGE -
        width,
    );

    const below = rect.top < 150;

    setPosition({
      left,
      width,
      top: below
        ? rect.bottom + 10
        : rect.top - 10,
      below,
      arrow: center - left,
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const close = () => {
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
      }
    };

    window.addEventListener(
      "scroll",
      close,
      true,
    );

    window.addEventListener(
      "resize",
      close,
    );

    window.addEventListener(
      "keydown",
      onKeyDown,
    );

    return () => {
      window.removeEventListener(
        "scroll",
        close,
        true,
      );

      window.removeEventListener(
        "resize",
        close,
      );

      window.removeEventListener(
        "keydown",
        onKeyDown,
      );
    };
  }, [open]);

  const tooltip =
    open &&
    position &&
    typeof document !== "undefined"
      ? createPortal(
          <span
            id={id}
            role="tooltip"
            className={[
              "simulator__tip",
              position.below
                ? "simulator__tip--below"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            style={{
              left: position.left,
              top: position.top,
              width: position.width,
              "--simulator-tip-arrow":
                `${position.arrow}px`,
            }}
          >
            {text}
          </span>,
          document.body,
        )
      : null;

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className="simulator__tip-trigger"
        aria-label={text}
        aria-describedby={
          open
            ? id
            : undefined
        }
        aria-expanded={open}
        onMouseEnter={() =>
          setOpen(true)
        }
        onMouseLeave={() =>
          setOpen(false)
        }
        onFocus={() =>
          setOpen(true)
        }
        onBlur={() =>
          setOpen(false)
        }
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();

          setOpen(
            (current) =>
              !current,
          );
        }}
      >
        <Info
          size={14}
          strokeWidth={2.2}
        />
      </button>

      {tooltip}
    </>
  );
}

export function FieldLabel({
  children,
  tip,
}) {
  return (
    <div className="simulator__label">
      <span>{children}</span>

      {tip && (
        <InfoTip text={tip} />
      )}
    </div>
  );
}
